import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Grid2, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { usePageTitle } from '@enroller/utils';
import { FormField, FormFieldType } from '@enroller/admissions-components';

import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext.tsx';
import { defaultPhotoValues, defaultValues, PhotoAlbumFormValues, photoAlbumSchema } from './PhotoAlbumFormValues.ts';
import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext.tsx';

const MIN_PHOTO_NUMBER = 3;
const MIN_PHOTO_TEXT = 'three';
const MAX_PHOTO_NUMBER = 5;
const MAX_CHARACTERS_DESCRIPTION = 1000;

export const PhotoAlbumForm = () => {
  return (
    <PageContainer>
      <Album />
    </PageContainer>
  );
};

const PageContainer = ({ children }: { children: ReactNode }) => {
  const { setPageTitle } = usePageTitle();
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, triggerErrors, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'Photo Album', subtitle: 'Photo 1' });
    setPageTitle('Photo Album');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? photoAlbumSchema.required : photoAlbumSchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const formMethods = useForm<PhotoAlbumFormValues>({
    mode: 'onChange',
    defaultValues,
    resolver,
  });

  const { handleSubmit, trigger } = formMethods;

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  const onSubmit = useCallback(
    () =>
      handleSubmit(
        (data) => {
          setApplicationPageData('photoAlbum', data);
          if (validationMode === ValidationType.Full) triggerErrors();
          setIsFormLoading(true);
          setTimeout(() => {
            console.log('Form submitted:', data);
            setIsFormLoading(false);
          }, 1000);
        },
        () => triggerErrors(),
      )(),
    [handleSubmit, setIsFormLoading, setApplicationPageData, triggerErrors, validationMode],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack gap={2} component="form" onSubmit={onSubmit}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </Stack>
  );
};

const Album = () => {
  const { control } = useFormContext<PhotoAlbumFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'additionalPhotos' });

  const handleAddPhoto = () => {
    append(defaultPhotoValues);
  };

  const handleRemovePhoto = (index: number) => {
    remove(index);
  };

  const isPhotoLimitReached = fields.length >= MAX_PHOTO_NUMBER;

  return (
    <>
      <PhotoPaper>
        <Grid2 size={12}>
          <Typography variant="subtitle1">Photo Album</Typography>
        </Grid2>
        <Grid2 size={12}>
          <AlbumInitialMessage />
        </Grid2>
        <Grid2 size={12}>
          <FormField id="photo1" label="Photo 1" control={control} name="photo1.photo" type={FormFieldType.PictureUploader} />
        </Grid2>
        <Grid2 size={4}>
          <FormField control={control} label="Photo Title" type={FormFieldType.Text} name="photo1.photoLabel" />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            rows={6}
            multiline
            control={control}
            label="Description"
            type={FormFieldType.Text}
            maxLength={MAX_CHARACTERS_DESCRIPTION}
            name="photo1.photoDescription"
          />
        </Grid2>
      </PhotoPaper>
      <PhotoPaper>
        <Grid2 size={12}>
          <FormField id="photo2" label="Photo 2" control={control} name="photo2.photo" type={FormFieldType.PictureUploader} />
        </Grid2>
        <Grid2 size={4}>
          <FormField control={control} label="Photo Title" type={FormFieldType.Text} name="photo2.photoLabel" />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            rows={6}
            multiline
            control={control}
            label="Description"
            type={FormFieldType.Text}
            maxLength={MAX_CHARACTERS_DESCRIPTION}
            name="photo2.photoDescription"
          />
        </Grid2>
      </PhotoPaper>
      <PhotoPaper>
        <Grid2 size={12}>
          <FormField id="photo3" label="Photo 3" control={control} name="photo3.photo" type={FormFieldType.PictureUploader} />
        </Grid2>
        <Grid2 size={4}>
          <FormField control={control} label="Photo Title" type={FormFieldType.Text} name="photo3.photoLabel" />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            rows={6}
            multiline
            control={control}
            label="Description"
            type={FormFieldType.Text}
            maxLength={MAX_CHARACTERS_DESCRIPTION}
            name="photo3.photoDescription"
          />
        </Grid2>
      </PhotoPaper>

      {fields.map((field, index) => {
        return (
          <PhotoPaper key={field.id}>
            <AdditionalPhoto index={index} />
            {index >= MIN_PHOTO_NUMBER && (
              <Grid2 size="auto">
                <Button variant="outlined" color="error" onClick={() => handleRemovePhoto(index)}>
                  Delete
                </Button>
              </Grid2>
            )}
          </PhotoPaper>
        );
      })}

      {isPhotoLimitReached && (
        <Grid2 container size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="caption" color="info">
              You have added maximum number of {MAX_PHOTO_NUMBER} photos.
            </Typography>
          </Alert>
        </Grid2>
      )}

      <Grid2 container size={12}>
        <Button startIcon={<AddIcon />} variant="outlined" color="primary" onClick={handleAddPhoto} disabled={isPhotoLimitReached}>
          Add Another Photo
        </Button>
      </Grid2>
    </>
  );
};

interface PhotoProps {
  index: number;
}

const AdditionalPhoto = ({ index }: PhotoProps) => {
  const { control } = useFormContext<PhotoAlbumFormValues>();

  return (
    <>
      <Grid2 size={4}>
        <FormField control={control} label="What is this Photo of?" type={FormFieldType.Text} name={`additionalPhotos.${index}.photoLabel`} />
      </Grid2>
      <Grid2 size={12}>
        <FormField id={`additionalPhoto-${index}`} label={''} control={control} name={`additionalPhotos.${index}.photo`} type={FormFieldType.PictureUploader} />
      </Grid2>

      <Grid2 size={12}>
        <FormField
          rows={6}
          multiline
          control={control}
          label="Description"
          type={FormFieldType.Text}
          maxLength={MAX_CHARACTERS_DESCRIPTION}
          name={`additionalPhotos.${index}.photoDescription`}
        />
      </Grid2>
    </>
  );
};

const AlbumInitialMessage = () => {
  return (
    <Alert variant="outlined" severity="info">
      <Stack gap={2}>
        <Typography variant="body2">
          Upload at least {MIN_PHOTO_TEXT} informal snapshots (more photos are encouraged), showing you and your family and friends in the places you live or
          frequently go. This album will help your host family understand how you live and the things you enjoy.
        </Typography>

        <Typography variant="body2">
          On the lines next to each picture, describe who and/or what is in the picture. If you and other young people are in the same photo, be sure to
          indicate which one is you. Do not upload collages; upload only one picture per spot. Be careful to avoid possibly offensive poses or dress. Do NOT
          pose with guns, alcoholic beverages or cigarettes in hand.
        </Typography>

        <Typography variant="body2" fontWeight="bold">
          MAKE SURE YOU ARE SMILING =)
        </Typography>
      </Stack>
    </Alert>
  );
};

const PhotoPaper = ({ children }: { children: ReactNode }) => {
  return (
    <Paper sx={{ p: 2, display: 'flex', gap: 2, flexDirection: 'column' }}>
      <Grid2 container spacing={3}>
        {children}
      </Grid2>
    </Paper>
  );
};
